import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../../services/authFetch';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { PageHeader } from '../shared/PageHeader';
import { EmptyState } from '../shared/EmptyState';
import { LoadingState } from '../shared/LoadingState';
import StatusBadge, { DocumentStatus } from '../shared/StatusBadge';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { useAttention, AttentionItem, TakeoverRequest } from '../../contexts/AttentionContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../config/authConfig';
import { formatAgeFromIso as formatAge } from '../../utils/formatAge';

/** A document in the analyst's review queue (server shape from GET /api/queue/documents). */
interface QueueItem {
  id: string;
  documentId: string;
  referenceNumber?: string;
  fileName: string;
  documentType: string;
  status: string;
  createdAt: string;
  processedAt?: string;
  fieldCount: number;
  requiredFieldCount: number;
  missingRequiredFields: number;
  avgConfidence: number;
  lockActive: boolean;
  lockedBy?: string;
  lockedByMe: boolean;
  lockedByName?: string;
  lockedMinutes: number;
  // Hold state (UX6) — held docs are visible to all with access, but only the sender (heldBy) and
  // receiver (assignedTo) may open them; others may only request a takeover.
  onHold: boolean;
  holdAssignedTo?: string;
  holdAssignedToMe: boolean;
  holdAssignedToName?: string;
  heldBy?: string;
  heldByMe: boolean;
  heldByName?: string;
  holdComment?: string;
  takeoverRequestedByMe: boolean;
  // Active-lock takeover (one-editor flow): true once the current user has asked the holder to
  // release an actively-locked document.
  iRequestedLockTakeover?: boolean;
}

/** A pending confirmation prompt (clear hold / reassign) with the action to run on confirm. */
type Confirm = {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  confirmColor?: 'primary' | 'warning' | 'error';
  action: () => void | Promise<void>;
} | null;

interface HomePageProps {
  /** Open the review workspace for a claimed document (routes to /documents/:id). */
  onDocumentClick?: (documentId: string) => void;
}

type Toast = { severity: 'info' | 'warning' | 'error'; message: string } | null;

type SortKey = 'createdAt' | 'processedAt' | 'documentType' | 'status' | 'missingRequiredFields';
type SortDir = 'asc' | 'desc';

/** Map the backend currentStatus string to a StatusBadge-compatible DocumentStatus value. */
function mapQueueStatus(item: QueueItem): DocumentStatus {
  if (item.onHold) return 'on_hold';
  if (item.lockActive && item.lockedByMe) return 'reviewing';
  if (item.lockActive) return 'in_progress';
  switch (item.status) {
    case 'PENDING_VALIDATION': return 'ready';
    case 'VALIDATION_IN_PROGRESS': return 'reviewing';
    case 'ON_HOLD': return 'on_hold';
    case 'FAILED': return 'error';
    case 'PROCESSING': return 'processing';
    default: return 'pending';
  }
}

interface AttentionRowProps {
  item: AttentionItem;
  busy: boolean;
  resuming: boolean;
  onResume: () => void;
  onClearHold: () => void;
  onReassign: (request: TakeoverRequest) => void;
}

/** One row in the "Needs my attention" list — either a held-for-me row (assigned, resume/clear) or a
 * takeover-request row (sender: reassign to a requester, or clear). */
function AttentionRow({ item: a, busy, resuming, onResume, onClearHold, onReassign }: AttentionRowProps) {
  return (
    <Box
      component="li"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {a.fileName}
          {a.documentType && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {a.documentType}
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {a.kind === 'assigned'
            ? `Held by ${a.heldByName || 'a colleague'}`
            : `You put this on hold for ${a.assignedToName || 'a reviewer'}`}
          {a.heldAt ? ` \u00b7 ${formatAge(a.heldAt)}` : ''}
        </Typography>
        {a.comment && (
          <Typography
            variant="body2"
            sx={{ fontStyle: 'italic', color: 'text.primary', borderLeft: 2, borderColor: 'divider', pl: 1, mb: 1 }}
          >
            {'\u201c'}{a.comment}{'\u201d'}
          </Typography>
        )}
        {a.kind === 'takeover_request' && (a.requests?.length ?? 0) > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Takeover requested by {a.requests!.map((r) => r.requestedByName || 'a colleague').join(', ')}
          </Typography>
        )}
      </Box>

      {a.kind === 'assigned' ? (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={busy}
            aria-label={`Resume review of ${a.fileName}`}
            onClick={onResume}
          >
            {resuming ? 'Resuming\u2026' : 'Resume'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={busy}
            aria-label={`Clear hold on ${a.fileName}`}
            onClick={onClearHold}
          >
            Clear hold
          </Button>
        </Stack>
      ) : (
        <Stack direction="column" spacing={1} sx={{ flexShrink: 0, alignItems: 'stretch' }}>
          {a.requests?.map((r) => (
            <Button
              key={r.requestedBy}
              size="small"
              variant="contained"
              disableElevation
              disabled={busy}
              aria-label={`Reassign ${a.fileName} to ${r.requestedByName || 'the requester'}`}
              onClick={() => onReassign(r)}
            >
              Reassign to {r.requestedByName || 'requester'}
            </Button>
          ))}
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={busy}
            aria-label={`Clear hold on ${a.fileName}`}
            onClick={onClearHold}
          >
            Clear hold
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export const HomePage: React.FC<HomePageProps> = ({ onDocumentClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<QueueItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { items: attentionItems, refresh: refreshAttention } = useAttention();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(ROLES.TENANT_ADMIN);

  const loadQueue = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setIsLoading(true);
      const response = await authFetch('/queue/documents');
      if (!response.ok) {
        setLoadFailed(true);
        return;
      }
      const json = await response.json();
      setItems(Array.isArray(json.data) ? json.data : []);
      setLoadFailed(false);
      setLastRefresh(new Date());
    } catch {
      setLoadFailed(true);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, []);

  // Initial load + polling (every 10s). Pauses when the tab is hidden to avoid wasting RU.
  useEffect(() => {
    loadQueue(true);

    const startPolling = () => {
      if (pollIntervalRef.current) return;
      pollIntervalRef.current = setInterval(() => loadQueue(false), 10_000);
    };
    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        loadQueue(false);
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadQueue]);

  /**
   * Claim a document (or the FIFO head when documentId is omitted) and open the workspace.
   * The backend enforces document-type access, FIFO order, and soft-lock takeover server-side.
   */
  const claim = useCallback(
    async (documentId?: string) => {
      setClaimingId(documentId ?? 'next');
      try {
        const response = await authFetch('/queue/claim', {
          method: 'POST',
          body: JSON.stringify({ documentId }),
        });
        const json = await response.json().catch(() => ({}));

        if (response.ok) {
          const claimed = json.data as QueueItem | null;
          if (claimed?.documentId) {
            onDocumentClick?.(claimed.documentId);
          } else {
            setToast({ severity: 'info', message: "You\u2019re all caught up \u2014 no documents waiting for review." });
            loadQueue(false);
          }
          return;
        }

        if (response.status === 409) {
          setToast({
            severity: 'warning',
            message: json.details?.lockedBy
              ? 'Another analyst is already reviewing this document.'
              : json.error || 'This document was just claimed by someone else.',
          });
        } else if (response.status === 403) {
          setToast({ severity: 'error', message: "You don\u2019t have access to this document type." });
        } else {
          setToast({ severity: 'error', message: json.error || 'Unable to open the document. Please try again.' });
        }
        loadQueue(false);
      } catch {
        setToast({ severity: 'error', message: 'Network error \u2014 could not open the document.' });
      } finally {
        setClaimingId(null);
      }
    },
    [onDocumentClick, loadQueue]
  );

  /**
   * Resume a held document (by its assignee or as a takeover). The backend returns it to review,
   * claimed by the caller; we then open the workspace. Used from both the attention list and the
   * held rows in the main queue.
   */
  const resume = useCallback(
    async (documentId: string) => {
      setClaimingId(documentId);
      try {
        const response = await authFetch(`/documents/${encodeURIComponent(documentId)}/resume`, { method: 'POST' });
        const json = await response.json().catch(() => ({}));
        if (response.ok) {
          refreshAttention();
          onDocumentClick?.(documentId);
          return;
        }
        if (response.status === 409) {
          setToast({ severity: 'warning', message: 'This document was already resumed by someone else.' });
        } else if (response.status === 403) {
          setToast({ severity: 'error', message: "You don\u2019t have access to this document type." });
        } else {
          setToast({ severity: 'error', message: json.error || 'Unable to resume the document. Please try again.' });
        }
        loadQueue(false);
        refreshAttention();
      } catch {
        setToast({ severity: 'error', message: 'Network error \u2014 could not resume the document.' });
      } finally {
        setClaimingId(null);
      }
    },
    [onDocumentClick, loadQueue, refreshAttention]
  );

  /** Refetch both the queue and the attention list right after a hold action (instant feedback). */
  const refreshAll = useCallback(() => {
    loadQueue(false);
    refreshAttention();
  }, [loadQueue, refreshAttention]);

  /**
   * POST a hold action (clear / reassign), toast the outcome, and refresh the
   * queue + attention list.
   */
  const runHoldAction = useCallback(
    async (endpoint: string, body: unknown, messages: { success: string; failDefault: string; networkError: string }) => {
      try {
        const response = await authFetch(endpoint, {
          method: 'POST',
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        const json = await response.json().catch(() => ({}));
        if (response.ok) {
          setToast({ severity: 'info', message: messages.success });
        } else {
          setToast({ severity: 'warning', message: json.error || messages.failDefault });
        }
      } catch {
        setToast({ severity: 'error', message: messages.networkError });
      } finally {
        refreshAll();
      }
    },
    [refreshAll]
  );

  /** Clear a hold (sender or receiver) — returns the document to the general queue. */
  const clearHold = useCallback(
    (documentId: string) =>
      runHoldAction(`/documents/${encodeURIComponent(documentId)}/hold/clear`, undefined, {
        success: 'Hold cleared \u2014 the document is back in the queue.',
        failDefault: 'Could not clear the hold. Please try again.',
        networkError: 'Network error \u2014 could not clear the hold.',
      }),
    [runHoldAction]
  );

  /** Reassign a hold to a requester (sender only) — hands the file to the person who asked for it. */
  const reassignHold = useCallback(
    (documentId: string, assignedTo: string) =>
      runHoldAction(`/documents/${encodeURIComponent(documentId)}/hold/reassign`, { assignedTo }, {
        success: 'Hold reassigned \u2014 the document is now with the requester.',
        failDefault: 'Could not reassign the hold. Please try again.',
        networkError: 'Network error \u2014 could not reassign the hold.',
      }),
    [runHoldAction]
  );

  /** Ask to take over a held document (for analysts who are not a party to the hold). */
  const requestHoldTakeover = useCallback(
    (documentId: string) =>
      runHoldAction(`/documents/${encodeURIComponent(documentId)}/hold/request-takeover`, undefined, {
        success: 'Takeover requested \u2014 the person who put it on hold has been notified.',
        failDefault: 'Could not request takeover. Please try again.',
        networkError: 'Network error \u2014 could not request takeover.',
      }),
    [runHoldAction]
  );

  /** Ask the current lock holder to release an actively-locked document (one-editor flow). */
  const requestReviewTakeover = useCallback(
    async (documentId: string) => {
      setClaimingId(documentId);
      try {
        const response = await authFetch('/queue/request-takeover', {
          method: 'POST',
          body: JSON.stringify({ documentId }),
        });
        const json = await response.json().catch(() => ({}));
        if (response.ok) {
          setToast({ severity: 'info', message: 'Requested \u2014 the reviewer has been asked to release it.' });
        } else if (response.status === 409) {
          setToast({ severity: 'warning', message: json.error || 'This document is no longer being reviewed \u2014 refresh and open it.' });
        } else {
          setToast({ severity: 'error', message: json.error || 'Could not request takeover. Please try again.' });
        }
        loadQueue(false);
      } catch {
        setToast({ severity: 'error', message: 'Network error \u2014 could not request takeover.' });
      } finally {
        setClaimingId(null);
      }
    },
    [loadQueue]
  );

  /** Open the confirm prompt for clearing a hold. */
  const promptClearHold = useCallback(
    (documentId: string, fileName: string) => {
      setConfirm({
        title: 'Clear this hold?',
        message: (
          <>
            This returns <strong>{fileName}</strong> to the general queue for anyone to pick up. The hold and its
            reason will be dismissed.
          </>
        ),
        confirmLabel: 'Clear hold',
        confirmColor: 'warning',
        action: () => clearHold(documentId),
      });
    },
    [clearHold]
  );

  /** Open the confirm prompt for reassigning a hold to a specific requester. */
  const promptReassignHold = useCallback(
    (documentId: string, fileName: string, request: TakeoverRequest) => {
      setConfirm({
        title: `Reassign to ${request.requestedByName || 'this reviewer'}?`,
        message: (
          <>
            This hands <strong>{fileName}</strong> to <strong>{request.requestedByName || 'the requester'}</strong>.
            They become the assigned reviewer and can open it.
          </>
        ),
        confirmLabel: 'Reassign',
        action: () => reassignHold(documentId, request.requestedBy),
      });
    },
    [reassignHold]
  );

  /** Run a confirmed action from the prompt, then close it. */
  const runConfirm = useCallback(async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.action();
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  }, [confirm]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = items.filter(
        (d) =>
          d.fileName?.toLowerCase().includes(q) ||
          d.documentType?.toLowerCase().includes(q) ||
          d.documentId?.toLowerCase().includes(q)
      );
    }
    // Sort — default is FIFO (createdAt asc).
    const dir = sortDir === 'asc' ? 1 : -1;
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case 'createdAt':
          return dir * ((new Date(a.createdAt).getTime() || 0) - (new Date(b.createdAt).getTime() || 0));
        case 'processedAt':
          return dir * ((new Date(a.processedAt || '').getTime() || 0) - (new Date(b.processedAt || '').getTime() || 0));
        case 'documentType':
          return dir * (a.documentType || '').localeCompare(b.documentType || '');
        case 'status': {
          // Sort by workflow priority (items needing attention first) rather than alphabetical.
          const statusPriority: Record<string, number> = {
            'FAILED': 0,
            'ON_HOLD': 1,
            'VALIDATION_IN_PROGRESS': 2,
            'PENDING_VALIDATION': 3,
            'PROCESSING': 4,
          };
          const pa = statusPriority[a.status] ?? 5;
          const pb = statusPriority[b.status] ?? 5;
          return dir * (pa - pb);
        }
        case 'missingRequiredFields':
          return dir * (a.missingRequiredFields - b.missingRequiredFields);
        default:
          return 0;
      }
    });
    return result;
  }, [items, searchTerm, sortKey, sortDir]);

  const handleSort = useCallback((key: SortKey) => {
    setSortDir((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
    setPage(0);
  }, [sortKey]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // The queue live-updates (polling, claims, releases) unlike a static report, so derive the
  // rendered page from the current item count rather than storing it — clamps back to the last
  // valid page automatically when the list shrinks under the user, with no extra effect.
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));
  const clampedPage = Math.min(page, pageCount - 1);

  const pagedItems = useMemo(
    () => filteredItems.slice(clampedPage * rowsPerPage, clampedPage * rowsPerPage + rowsPerPage),
    [filteredItems, clampedPage, rowsPerPage]
  );

  // Is anything claimable right now (drives the "Start next" button)? Held docs and docs actively
  // locked by someone else are not — they are resumed / taken over explicitly.
  const hasClaimable = useMemo(
    () => items.some((d) => !d.onHold && !(d.lockActive && !d.lockedByMe)),
    [items]
  );

  const busy = claimingId !== null;

  const renderCompleteness = (item: QueueItem) => {
    if (item.requiredFieldCount === 0) {
      return (
        <Typography variant="body2" color="text.disabled">{'\u2014'}</Typography>
      );
    }
    const filled = item.requiredFieldCount - item.missingRequiredFields;
    const allComplete = item.missingRequiredFields === 0;
    return (
      <Tooltip title={allComplete ? 'All required fields extracted' : `${item.missingRequiredFields} required field(s) missing`}>
        <Chip
          size="small"
          variant="outlined"
          color={allComplete ? 'success' : 'warning'}
          icon={allComplete ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
          label={`${filled}/${item.requiredFieldCount}`}
        />
      </Tooltip>
    );
  };

  /** Inline status indicator + contextual action for non-standard states. */
  const renderRowStatus = (item: QueueItem) => {
    if (item.onHold) {
      const label = item.holdAssignedToMe
        ? 'On hold \u2014 for you'
        : item.heldByMe
          ? `On hold \u2014 sent to ${item.holdAssignedToName || 'a reviewer'}`
          : `On hold \u2014 by ${item.heldByName || 'another reviewer'}`;
      const chip = (
        <Tooltip title={item.holdComment ? `Reason: ${item.holdComment}` : ''}>
          <Chip
            size="small"
            variant="outlined"
            color="default"
            icon={<PauseCircleOutlineIcon fontSize="small" />}
            label={label}
          />
        </Tooltip>
      );
      // If I'm not a party (not the sender, not the receiver), offer appropriate actions.
      if (!item.holdAssignedToMe && !item.heldByMe) {
        // Admins can directly clear or resume the hold.
        if (isAdmin) {
          return (
            <>
              {chip}
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  aria-label={`Resume review of ${item.fileName}`}
                  onClick={(e) => { e.stopPropagation(); resume(item.documentId); }}
                  sx={{ px: 0.5, py: 0, minWidth: 0, fontSize: 'caption.fontSize', textTransform: 'none' }}
                >
                  Resume
                </Button>
                <Button
                  size="small"
                  variant="text"
                  color="warning"
                  disabled={busy}
                  aria-label={`Clear hold on ${item.fileName}`}
                  onClick={(e) => { e.stopPropagation(); promptClearHold(item.documentId, item.fileName); }}
                  sx={{ px: 0.5, py: 0, minWidth: 0, fontSize: 'caption.fontSize', textTransform: 'none' }}
                >
                  Clear hold
                </Button>
              </Stack>
            </>
          );
        }
        return (
          <>
            {chip}
            <Button
              size="small"
              variant="text"
              disabled={busy || item.takeoverRequestedByMe}
              onClick={(e) => { e.stopPropagation(); requestHoldTakeover(item.documentId); }}
              sx={{ mt: 0.5, px: 0.5, py: 0, minWidth: 0, fontSize: 'caption.fontSize', textTransform: 'none' }}
            >
              {item.takeoverRequestedByMe ? 'Takeover requested' : 'Request takeover'}
            </Button>
          </>
        );
      }
      return chip;
    }
    if (item.lockActive && !item.lockedByMe) {
      return (
        <>
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            icon={<LockOutlinedIcon fontSize="small" />}
            label={`In review \u2014 ${item.lockedByName || 'another analyst'} \u00b7 ${item.lockedMinutes} min`}
          />
          <Button
            size="small"
            variant="text"
            disabled={busy || !!item.iRequestedLockTakeover}
            onClick={(e) => { e.stopPropagation(); requestReviewTakeover(item.documentId); }}
            sx={{ mt: 0.5, px: 0.5, py: 0, minWidth: 0, fontSize: 'caption.fontSize', textTransform: 'none' }}
          >
            {item.iRequestedLockTakeover ? 'Hand-off requested' : 'Request hand-off'}
          </Button>
        </>
      );
    }
    if (item.lockedByMe) {
      return (
        <Chip
          size="small"
          variant="outlined"
          color="info"
          icon={<RateReviewOutlinedIcon fontSize="small" />}
          label={"You\u2019re reviewing"}
        />
      );
    }
    return null;
  };

  return (
    <Box>
      <PageHeader
        title="Review Queue"
        subtitle="Documents waiting for your verification, oldest first"
        action={
          <Tooltip
            title={!busy && !hasClaimable && items.length > 0 ? 'All waiting documents are currently being reviewed' : ''}
          >
            {/* span wrapper lets the tooltip show while the button is disabled */}
            <span>
              <Button
                variant="contained"
                disableElevation
                startIcon={<PlayArrowIcon />}
                onClick={() => claim()}
                disabled={busy || !hasClaimable}
              >
                {claimingId === 'next' ? 'Starting\u2026' : 'Start next'}
              </Button>
            </span>
          </Tooltip>
        }
      />

      {/* Needs my attention — documents put on hold and addressed to me (UX6). Only shown when
          there is something to act on, so the queue stays the primary surface when all clear. */}
      {attentionItems.length > 0 && (
        <Box sx={{ px: 3, pt: 3 }}>
          <Paper variant="outlined" sx={{ borderColor: 'warning.main' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <PauseCircleOutlineIcon color="warning" aria-hidden="true" />
              <Typography variant="h6" component="h2">
                Needs my attention ({attentionItems.length})
              </Typography>
            </Box>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {attentionItems.map((a) => (
                <AttentionRow
                  key={a.id}
                  item={a}
                  busy={busy}
                  resuming={claimingId === a.documentId}
                  onResume={() => resume(a.documentId)}
                  onClearHold={() => promptClearHold(a.documentId, a.fileName)}
                  onReassign={(request) => promptReassignHold(a.documentId, a.fileName, request)}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Search + refresh */}
      <Box sx={{ px: 3, pt: 3, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder={"Search by file name, type, or ID\u2026"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 400, m: 0 }}
          size="small"
          inputProps={{ 'aria-label': 'Search the review queue' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          size="small"
          onClick={() => loadQueue(true)}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing\u2026' : 'Refresh'}
        </Button>
      </Box>

      {/* Queue table */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" component="h2">
              Waiting for review ({filteredItems.length})
            </Typography>
            {lastRefresh && (
              <Typography variant="caption" color="text.secondary">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
          </Box>

          {/* Top pagination */}
          {filteredItems.length > 0 && (
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={clampedPage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}

          <TableContainer>
            <Table aria-label="Documents waiting for review">
              <TableHead>
                <TableRow>
                  <TableCell>Reference #</TableCell>
                  <TableCell>File Name</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'documentType'}
                      direction={sortKey === 'documentType' ? sortDir : 'asc'}
                      onClick={() => handleSort('documentType')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'processedAt'}
                      direction={sortKey === 'processedAt' ? sortDir : 'asc'}
                      onClick={() => handleSort('processedAt')}
                    >
                      Processed
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === 'createdAt'}
                      direction={sortKey === 'createdAt' ? sortDir : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      Waiting
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortKey === 'status'}
                      direction={sortKey === 'status' ? sortDir : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortKey === 'missingRequiredFields'}
                      direction={sortKey === 'missingRequiredFields' ? sortDir : 'asc'}
                      onClick={() => handleSort('missingRequiredFields')}
                    >
                      Required Completeness
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ border: 0 }}>
                      <LoadingState message="Loading your review queue" />
                    </TableCell>
                  </TableRow>
                ) : loadFailed ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ border: 0 }}>
                      <EmptyState
                        icon={<InboxOutlinedIcon sx={{ fontSize: 48 }} />}
                        message={"Couldn\u2019t load the review queue"}
                        description="Something went wrong reaching the server. Try refreshing in a moment."
                        action={
                          <Button variant="contained" disableElevation onClick={() => loadQueue(true)}>
                            Retry
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ border: 0 }}>
                      <EmptyState
                        icon={<InboxOutlinedIcon sx={{ fontSize: 48 }} />}
                        message={searchTerm ? 'No matches' : "You\u2019re all caught up"}
                        description={
                          searchTerm
                            ? 'No documents match your search. Try adjusting the terms.'
                            : "There are no documents waiting for your review right now. New documents assigned to your document types will appear here automatically."
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedItems.map((item) => {
                    const openable = !item.onHold && !(item.lockActive && !item.lockedByMe);
                    const statusChip = renderRowStatus(item);
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        tabIndex={0}
                        role="row"
                        onClick={openable && !busy ? () => claim(item.documentId) : undefined}
                        onKeyDown={openable && !busy ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            claim(item.documentId);
                          }
                        } : undefined}
                        sx={{ cursor: openable && !busy ? 'pointer' : 'default' }}
                      >
                        <TableCell>
                          <Typography variant="body2" color={item.referenceNumber ? 'text.primary' : 'text.disabled'}>
                            {item.referenceNumber || '\u2014'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.fileName}
                          </Typography>
                          {statusChip && <Box sx={{ mt: 0.5 }}>{statusChip}</Box>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.documentType || 'Unknown'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.processedAt
                              ? new Date(item.processedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                              : '\u2014'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatAge(item.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <StatusBadge type="document-status" status={mapQueueStatus(item)} />
                        </TableCell>
                        <TableCell align="center">
                          {renderCompleteness(item)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Bottom pagination */}
          {filteredItems.length > 0 && (
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={clampedPage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Paper>
      </Box>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Shared confirmation prompt for clear-hold / reassign (UX6). */}
      <ConfirmDialog
        open={confirm !== null}
        titleId="hold-confirm-title"
        title={confirm?.title}
        message={confirm?.message}
        busy={confirmBusy}
        onCancel={() => setConfirm(null)}
        confirmLabel={confirmBusy ? 'Working\u2026' : confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor || 'primary'}
        onConfirm={runConfirm}
      />
    </Box>
  );
};
